import { useState } from "react";
import Flag from "react-world-flags";
import { MODAL_STYLES } from "../../constants/modal-styles";
import { ModalButton } from "./ModalButton";

interface WelcomeModalProps {
  isOpen: boolean;
  language: "ka" | "en";
  onLanguageChange: (lang: "ka" | "en") => void;
  onClose: () => void;
}

export const WelcomeModal = ({
  isOpen,
  language,
  onLanguageChange,
  onClose,
}: WelcomeModalProps) => {
  const [step, setStep] = useState<"language" | "rules">("language");

  if (!isOpen) return null;

  const handleLanguageSelect = (lang: "ka" | "en") => {
    onLanguageChange(lang);
    setStep("rules");
  };

  const handleClose = () => {
    setStep("language");
    onClose();
  };

  const content = {
    ka: {
      title: "Treasure Hunt",
      description: "გაიცანით, შეადგინეთ შეტყობინებები და მოიპოვეთ საჩუქრები!",
      rules: [
        "შეიყვანეთ კოდი და აირჩიეთ მოქმედება",
        "დააწკაპუნეთ ღრუბელზე შინაარსის გასახსნელად",
        "თითოეული კოდით შეგიძლიათ გახსნათ მხოლოდ 1 უჯრედი",
        "იპოვეთ შეტყობინებები, გაიცანით ადამიანები ან მოიპოვეთ პრიზები",
        "თუ ღრუბელი ცარიელია, გამოჩნდება კომპასი უახლოეს სუნდუკამდე",
        "ასევე შეგიძლიათ დატოვოთ საკუთარი შეტყობინება ან გაცნობა",
      ],
      startGame: "დაწყება",
    },
    en: {
      title: "Treasure Hunt",
      description: "Meet people, leave messages and win prizes!",
      rules: [
        "Enter code and choose action",
        "Click on a cloud to reveal content",
        "Each code can open only 1 cell",
        "Find messages, meet people or get prizes",
        "If cloud is empty, compass appears to nearest chest",
        "You can also leave your own message or acquaintance",
      ],
      startGame: "Start Game",
    },
  };

  const text = content[language] || content.ka;

  return (
    <div
      className={MODAL_STYLES.overlay}
      onClick={onClose}
    >
      <div
        className="relative max-w-md w-full bg-amber-100 rounded-xl border-2 border-amber-800 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{
          fontFamily: MODAL_STYLES.fontFamily,
        }}
      >
        <div className={MODAL_STYLES.content}>
          {step === "language" ? (
            <>
              {/* Выбор языка */}
              <div className="flex justify-center gap-4 sm:gap-8">
                <button
                  onClick={() => handleLanguageSelect("ka")}
                  className="flex flex-col items-center gap-2 transition-all cursor-pointer opacity-60 hover:opacity-100 hover:scale-110"
                >
                  <Flag code="GE" className="w-12 h-8 sm:w-16 sm:h-10 rounded shadow-lg" />
                  <span className="text-sm sm:text-base font-bold text-amber-900">
                    ქართული
                  </span>
                </button>
                <button
                  onClick={() => handleLanguageSelect("en")}
                  className="flex flex-col items-center gap-2 transition-all cursor-pointer opacity-60 hover:opacity-100 hover:scale-110"
                >
                  <Flag code="GB" className="w-12 h-8 sm:w-16 sm:h-10 rounded shadow-lg" />
                  <span className="text-sm sm:text-base font-bold text-amber-900">
                    English
                  </span>
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Правила игры */}
              <h2 className="text-base sm:text-lg font-bold text-amber-900 mb-3 text-center">
                {text.title}
              </h2>

              {/* Описание игры */}
              <p className="text-xs sm:text-sm text-amber-800 text-center mb-4 italic">
                {text.description}
              </p>

              {/* Правила */}
              <div className="space-y-2 mb-4">
                {text.rules.map((rule, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <span className="text-amber-700 font-bold text-sm flex-shrink-0">
                      {index + 1}.
                    </span>
                    <p className="text-xs sm:text-sm">{rule}</p>
                  </div>
                ))}
              </div>

              <ModalButton onClick={handleClose}>
                <span className="text-xs sm:text-sm">{text.startGame}</span>
              </ModalButton>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
